import React from 'react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react';
import {
	ArrowLink,
	H3,
	ITheme,
	Shimmer,
	ShimmerPlaceholderText,
} from '../../../../ui';
import fakeTierBadge from '../../../../../assets/loyalty/fake-tier.svg?reference';
import { SolidContentWrap } from '../../../shared/components';
import { CurrentTierPointsTitle } from './CurrentTierPointsTitle';
import { useTranslation } from '../../../shared/i18n/translate/useTranslation';
import { useLoyaltyAccountStore } from '../../domain/stores/useLoyaltyAccountStore';
import { useGetFromTierRedirectLink } from '../account-overview/hooks/useGetFromTierRedirectLink';
import { useLoyaltyTierStore } from '../../domain/stores/useLoyaltyTierStore';
import { StyledUserTierBadge } from './UserTierBadge';
import Responsive from '../../../../ui/utils/Responsive';
import themed from '../../../../ui/themer/themed';

export const UserCurrentTier = observer(() => {
	const { t } = useTranslation();
	const { loyaltyDetails, isLoading } = useLoyaltyAccountStore();
	const { contentfulTierDetails } = useLoyaltyTierStore();
	const { redirectLink, redirectLabel } = useGetFromTierRedirectLink(t);

	if (isLoading) {
		return <StyledUserCurrentTierShimmer />;
	}
	if (!loyaltyDetails) {
		return null;
	}

	const currentTierBadge = contentfulTierDetails
		? contentfulTierDetails?.image.fields.file.url
		: fakeTierBadge;

	return (
		<StyledUserCurrentTierContainer>
			{currentTierBadge && (
				<StyledUserTierBadge src={currentTierBadge} alt="" />
			)}
			<StyledUserCurrentTierContent>
				{!!contentfulTierDetails && (
					<CurrentTierPointsTitle title={contentfulTierDetails?.title} />
				)}
				<StyledArrowLink href={redirectLink}>{redirectLabel}</StyledArrowLink>
			</StyledUserCurrentTierContent>
		</StyledUserCurrentTierContainer>
	);
});

const StyledUserCurrentTierContainer = styled(SolidContentWrap)`
	display: flex;
	gap: 16px;
	align-items: center;
	flex-grow: 1;
	padding: 42px 0;
	padding-left: 24px;

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		width: 100%;
		padding: 24px 12px;
	}
`;

const StyledUserCurrentTierContent = styled.div`
	width: 50%;
	gap: 6px;
`;

export const StyledTitleShimmer = styled(Shimmer.withComponent(H3))<{}, ITheme>`
	height: 22px;
	background: ${themed(({ background }) => background.light)};
`;
const StyledArrowLink = styled(ArrowLink)`
	cursor: pointer;
`;

const StyledLinkShimmer = styled(Shimmer.withComponent(ShimmerPlaceholderText))<
	{},
	ITheme
>`
	height: 12px;
	background: ${themed(({ background }) => background.light)};
`;

const StyledUserCurrentTierShimmer = () => (
	<StyledUserCurrentTierContainer>
		<Shimmer>
			<StyledUserTierBadge src={fakeTierBadge} alt="" />
		</Shimmer>
		<StyledUserCurrentTierContent>
			<StyledTitleShimmer />
			<StyledLinkShimmer />
		</StyledUserCurrentTierContent>
	</StyledUserCurrentTierContainer>
);
